import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Grid } from '@wds/grid';
import { Button } from '@wds/button';
import { IconCheckCircle, IconClone } from '@wds/icons';

import PropTypes from 'prop-types';
import STRINGS from '../constants';
import MFAErrorMessage from './MFAErrorMessage';

const RecoveryCodes = (props) => {
	const {
		codes, error, finishBtnTxt, handleOnFinish, handleError, showFinishButton,
	} = props;
	const [copiedSuccessfully, setCopiedSuccessfully] = useState(false);
	const timeoutRef = useRef(null);

	useEffect(() => {
		// Track recovery codes viewed
		if (window.track && codes?.length > 0) {
			window.track('mfaRecoveryCodesViewed', {
				context: 'settings',
				codesCount: codes.length
			});
		}
	}, [codes]);

	useEffect(() => () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
	}, []);

	const handleCopyResult = (result) => {
		if (result === 'success') {
			setCopiedSuccessfully(true);
			setTimeout(() => {
				setCopiedSuccessfully(false);
			}, 1000);
		} else {
			setCopiedSuccessfully(false);
			handleError(STRINGS.copyFailed);
		}
	};

	const CopyToClipboard = () => {
		// Track recovery codes copy attempt
		if (window.track) {
			window.track('mfaRecoveryCodesCopied', {
				context: 'settings'
			});
		}
		
		// eslint-disable-next-line no-undef
		if (navigator.clipboard) {
			// eslint-disable-next-line no-undef
			navigator.clipboard.writeText(codes?.join('\n'))
				.then(() => {
					handleCopyResult('success');
				})
				.catch(() => {
					handleCopyResult('error');
				});
		} else {
			handleCopyResult('error');
		}
	};

	return (
		<Box
			display='flex'
			flexDirection='column'
			gap={4}
		>
			<MFAErrorMessage error={error} />
			<Typography variant='sectionTitleSm' weight='medium'>
				{STRINGS.recoveryCodes}
			</Typography>
			<Typography variant='bodySm'>
				{STRINGS.recoveryCodeDescription}
			</Typography>
			<Box display='flex' justifyContent='center' alignItems='center'>
				<Box style={{ width: 275, height: 'auto', backgroundColor: '#F0F0F0', borderRadius: 8 }} py={2}>
					<Grid>
						{codes.map((code) => (
							<Grid.Item xs={4} key={`recovery-code-${code}`}>
								<Typography variant='bodySm'>
									{code}
								</Typography>
							</Grid.Item>
						))}
					</Grid>
				</Box>
				<Box display='flex' justifyContent='right' alignItems='center'>
					<Button variant='text' title='Copy codes to clipboard' onClick={CopyToClipboard}>
						{copiedSuccessfully ? (
							<IconCheckCircle
								aria-hidden
								focusable
							/>
						) : (
							<IconClone
								aria-hidden
								focusable
								color='secondary'
							/>
						)}
					</Button>
				</Box>
			</Box>
			<Typography component='p' variant='bodySm'>
				<Typography component='span' weight='medium'>
					{STRINGS.keepCodesSafe}
				</Typography>
				{' '}
				{STRINGS.limitedVisibility}
			</Typography>
			{showFinishButton && (
				<Button
					id='next-button'
					size='lg'
					onClick={handleOnFinish}
				>
					{finishBtnTxt}
				</Button>
			)}
		</Box>
	);
};

RecoveryCodes.propTypes = {
	codes: PropTypes.arrayOf(PropTypes.string).isRequired,
	error: PropTypes.string,
	handleOnFinish: PropTypes.func,
	finishBtnTxt: PropTypes.string,
	showFinishButton: PropTypes.bool,
};

RecoveryCodes.defaultProps = {
	error: '',
	finishBtnTxt: STRINGS.finish,
	handleOnFinish: () => {},
	showFinishButton: true,
};

export default RecoveryCodes;
